// @workflow assistant.clarification
/**
 * Stepping through a clarification's questions (Khalid, 2026-10-14: like Claude's AskUserQuestion).
 * The panel and the composer share this one state: which question is on screen and a draft per
 * question. A single-select pick moves to the next question; on a one-question clarification it
 * is the answer at once, as before. Nothing is sent before Submit, and every answer can be changed
 * until then. Every function returns a new state.
 * No Vue or path-alias imports, so `npm run test:unit` (node --test) can load it directly.
 */
import { buildAnswer } from './clarification.js';
import { buildAnswers } from './clarificationAnswers.js';

const EMPTY_DRAFT = Object.freeze({ optionIds: [], text: '' });

/** @returns {{ index: number, drafts: Record<string, { optionIds: string[], text: string }> }} */
export const startSteps = () => ({ index: 0, drafts: {} });

const clampIndex = (index, questions) => Math.min(Math.max(0, index), Math.max(0, questions.length - 1));
const current = (steps, questions) => questions[clampIndex(steps.index, questions)];

/** The draft of the question on screen: its picks and its typed text. */
export function draftOf(steps, questions) {
  const q = current(steps, questions);
  return (q && steps.drafts[q.id]) || EMPTY_DRAFT;
}

const withCurrentDraft = (steps, questions, draft) => {
  const q = current(steps, questions);
  return { ...steps, drafts: { ...steps.drafts, [q.id]: { optionIds: [...draft.optionIds], text: draft.text } } };
};

const isAnswered = (steps, q) => buildAnswer(q, steps.drafts[q.id] || EMPTY_DRAFT) !== null;

/** Shows another question (Back, Next, ← →); drafts stay. */
export const goToStep = (steps, questions, index) => ({ ...steps, index: clampIndex(index, questions) });

/** What the user typed in the composer answers the question on screen (where free text is allowed). */
export const withTypedText = (steps, questions, text) =>
  withCurrentDraft(steps, questions, { ...draftOf(steps, questions), text: typeof text === 'string' ? text : '' });

/**
 * A pick on the question on screen. Multi-select toggles and stays. Single-select records the
 * pick and moves on; on a one-question clarification the pick (with any typed text) is the answer.
 * @returns {{ steps: object, submit: object|null }} `submit`: the answer to send now, else null
 */
export function pickOption(steps, questions, optionId) {
  const q = current(steps, questions);
  const draft = draftOf(steps, questions);
  if (q.multiSelect) {
    const optionIds = draft.optionIds.includes(optionId) ? draft.optionIds.filter((id) => id !== optionId) : [...draft.optionIds, optionId];
    return { steps: withCurrentDraft(steps, questions, { ...draft, optionIds }), submit: null };
  }
  const picked = withCurrentDraft(steps, questions, { ...draft, optionIds: [optionId] });
  if (questions.length === 1) return { steps: picked, submit: buildAnswers(questions, picked.drafts) };
  const isLast = steps.index >= questions.length - 1;
  return { steps: isLast ? picked : goToStep(picked, questions, steps.index + 1), submit: null };
}

/** The answer Submit sends, or null while a question has none. */
export const submission = (steps, questions) => buildAnswers(questions, steps.drafts);

/** What the panel shows: which question, how many, which have an answer, and whether Submit is on. */
export function stepsView(steps, questions) {
  const index = clampIndex(steps.index, questions);
  const answered = questions.map((q) => isAnswered(steps, q));
  const answeredCount = answered.filter(Boolean).length;
  return {
    index,
    count: questions.length,
    question: questions[index],
    isFirst: index === 0,
    isLast: index === questions.length - 1,
    answered,
    answeredCount,
    canSubmit: questions.length > 0 && answeredCount === questions.length,
  };
}
